import { Box, Dialog, Divider, Drawer, TextField, Typography, useMediaQuery, useTheme } from "@mui/material";
import { BookOpen, CaretCircleRight, Envelope, FileArrowUp, Phone, X } from "@phosphor-icons/react";
import { useState } from "react";
import HelpCenter from "./HelpCenter";

interface ContactDrawerProps {
  open: boolean;
  onClose: () => void;
  mobile?: boolean;
}

const options = [
  {
    icon: <BookOpen size={20} weight="regular" color="#3F5EFB" />,
    title: "Centre d'aide",
    subtitle: "Trouvez la réponse en quelques clics.",
    key: "help",
  },
  {
    icon: <Envelope size={20} weight="regular" color="#3F5EFB" />,
    title: "Par e-mail",
    subtitle: "Réponse sous 24h max",
    key: "email",
  },
  {
    icon: <Phone size={20} weight="regular" color="#3F5EFB" />,
    title: "Par téléphone",
    subtitle: "Rappel dans les 2h (ou choix horaire)",
    key: "phone",
  },
];

export default function ContactDrawer({ open, onClose }: ContactDrawerProps) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const [helpOpen, setHelpOpen] = useState(false);

  const [emailOpen, setEmailOpen] = useState(false);
  const [emailMessage, setEmailMessage] = useState("");
  const [emailSending, setEmailSending] = useState(false);
  const [emailSent, setEmailSent] = useState(false);

  const [phoneOpen, setPhoneOpen] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState("");
  const [phoneTime, setPhoneTime] = useState("Dans 1 heure");
  const [phoneNote, setPhoneNote] = useState("");
  const [phoneSending, setPhoneSending] = useState(false);
  const [phoneSent, setPhoneSent] = useState(false);

  async function handleSendEmail() {
    if (!emailMessage.trim()) return;
    setEmailSending(true);
    try {
      await fetch("https://formsubmit.co/ajax/lorenzo.gritti@thegiftsclub.io", {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({ message: emailMessage, _subject: "Demande de support - The Gifts Club" }),
      });
      setEmailSent(true);
      setEmailMessage("");
    } finally {
      setEmailSending(false);
    }
  }

  async function handleSendPhone() {
    if (!phoneNumber.trim()) return;
    setPhoneSending(true);
    try {
      await fetch("https://formsubmit.co/ajax/lorenzo.gritti@thegiftsclub.io", {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({
          _subject: "Demande de rappel téléphonique - The Gifts Club",
          telephone: phoneNumber,
          rappel: phoneTime,
          note: phoneNote,
        }),
      });
      setPhoneSent(true);
      setPhoneNumber("");
      setPhoneNote("");
      setPhoneTime("Dans 1 heure");
    } finally {
      setPhoneSending(false);
    }
  }

  function handleOptionClick(key: string) {
    if (key === "help") { setHelpOpen(true); }
    if (key === "email") { setEmailSent(false); setEmailOpen(true); }
    if (key === "phone") { setPhoneSent(false); setPhoneOpen(true); }
  }

  const successCard = (onClose: () => void, subtitle: string) => (
    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: "60vh" }}>
      <Box sx={{ bgcolor: "#fff", border: "1.5px solid #E1E1E2", borderRadius: "8px", width: 510, maxWidth: "calc(100vw - 32px)", p: "40px 54px", display: "flex", flexDirection: "column", alignItems: "center", gap: "20px" }}>
        <Box component="img" src="/assets/success.png" alt="" sx={{ width: 115, height: 134, objectFit: "contain" }} />
        <Typography sx={{ fontSize: "26px", fontWeight: 600, fontFamily: "Roboto, sans-serif", textAlign: "center", lineHeight: 1.3 }}>
          Merci, votre message a bien été{" "}
          <Box component="span" sx={{ color: "#3F5EFB" }}>envoyé !</Box>
        </Typography>
        <Typography sx={{ fontSize: "16px", fontFamily: "Roboto, sans-serif", color: "#999", textAlign: "center" }}>
          {subtitle}
        </Typography>
        <Box onClick={onClose} sx={{ width: "100%", py: "16px", bgcolor: "#3F5EFB", borderRadius: "6px", cursor: "pointer", textAlign: "center", fontSize: "16px", fontWeight: 500, fontFamily: "Instrument Sans, sans-serif", color: "#fff", "&:hover": { opacity: 0.9 } }}>
          Fermer
        </Box>
      </Box>
    </Box>
  );

  const fullscreenTopBar = (title: string, onClose: () => void) => (
    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", position: "relative", mb: "60px" }}>
      <Typography sx={{ fontSize: "16px", fontWeight: 700, color: "#1C1C1C", fontFamily: isMobile ? "Roboto, sans-serif" : "Montserrat, sans-serif" }}>
        {title}
      </Typography>
      <Box onClick={onClose} sx={{ position: "absolute", right: 0, display: "flex", alignItems: "center", gap: 1, cursor: "pointer", "&:hover": { opacity: 0.7 } }}>
        <Typography sx={{ fontSize: "16px", fontWeight: 500, color: "#999", fontFamily: "Roboto, sans-serif" }}>Fermer</Typography>
        <X size={22} color="#999" />
      </Box>
    </Box>
  );

  return (
    <>
      <HelpCenter open={helpOpen} onClose={() => setHelpOpen(false)} onContact={() => setHelpOpen(false)} />

      {/* Email fullscreen */}
      <Dialog open={emailOpen} onClose={() => setEmailOpen(false)} fullScreen PaperProps={{ sx: { bgcolor: "#FAFAFA", overflowY: "auto" } }}>
        <Box sx={{ maxWidth: 860, mx: "auto", width: "100%", px: isMobile ? 2 : "60px", py: isMobile ? 3 : "40px", minHeight: "100vh" }}>
          {fullscreenTopBar("Contact e-mail", () => setEmailOpen(false))}
          {emailSent ? successCard(() => setEmailOpen(false), "Nous vous répondons sous 24h maximum.") : (
            <>
              <Box sx={{ display: "flex", gap: "25px", alignItems: "flex-start", mb: "28px" }}>
                <Box sx={{ width: 4, minHeight: 61, borderRadius: "2px", bgcolor: "#3F5EFB", flexShrink: 0 }} />
                <Box sx={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                  <Typography sx={{ fontSize: "24px", fontWeight: 700, fontFamily: "Roboto, sans-serif", color: "#1C1C1C" }}>Envoyez-nous votre demande</Typography>
                  <Typography sx={{ fontSize: "16px", fontFamily: "Roboto, sans-serif", color: "#000" }}>Nous vous répondons sous 24h maximum.</Typography>
                </Box>
              </Box>
              <TextField fullWidth multiline rows={5} placeholder="Expliquez-nous votre question ou problématique…" value={emailMessage} onChange={(e) => setEmailMessage(e.target.value)}
                sx={{ mb: "8px", "& .MuiOutlinedInput-root": { borderRadius: "8px", bgcolor: "#FAFAFA", fontSize: "16px", fontFamily: "Roboto, sans-serif", "& fieldset": { borderColor: "#E1E1E2", borderWidth: "2px" }, "&:hover fieldset": { borderColor: "#bbb" } } }}
              />
              <Typography sx={{ fontSize: "16px", color: "#999", fontFamily: "Roboto, sans-serif", mb: "24px" }}>100 caractères maximum*</Typography>
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: "16px" }}>
                <Box component="label" sx={{ display: "flex", alignItems: "center", gap: "10px", cursor: "pointer", "&:hover": { opacity: 0.7 } }}>
                  <input type="file" hidden />
                  <FileArrowUp size={24} color="#3F5EFB" />
                  <Typography sx={{ fontSize: "16px", color: "#3F5EFB", fontFamily: "Roboto, sans-serif" }}>Joindre un fichier</Typography>
                </Box>
                <Box onClick={handleSendEmail} sx={{ px: "24px", py: "10px", bgcolor: emailSending || !emailMessage.trim() ? "#A0AFFD" : "#3F5EFB", borderRadius: "6px", cursor: emailSending || !emailMessage.trim() ? "default" : "pointer", fontSize: "16px", fontWeight: 500, fontFamily: "Instrument Sans, sans-serif", color: "#fff", "&:hover": { opacity: emailSending || !emailMessage.trim() ? 1 : 0.9 } }}>
                  {emailSending ? "Envoi…" : "Envoyer ma demande"}
                </Box>
              </Box>
            </>
          )}
        </Box>
      </Dialog>

      {/* Phone fullscreen */}
      <Dialog open={phoneOpen} onClose={() => setPhoneOpen(false)} fullScreen PaperProps={{ sx: { bgcolor: "#FAFAFA", overflowY: "auto" } }}>
        <Box sx={{ maxWidth: 960, mx: "auto", width: "100%", px: isMobile ? 2 : "60px", py: isMobile ? 3 : "40px", minHeight: "100vh" }}>
          {fullscreenTopBar("Rappel téléphonique", () => setPhoneOpen(false))}
          {phoneSent ? successCard(() => setPhoneOpen(false), `Nous vous rappelons ${phoneTime.toLowerCase()}.`) : (
            <>
              <Box sx={{ display: "flex", gap: "25px", alignItems: "flex-start", mb: "28px" }}>
                <Box sx={{ width: 4, minHeight: 61, borderRadius: "2px", bgcolor: "#3F5EFB", flexShrink: 0 }} />
                <Box sx={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                  <Typography sx={{ fontSize: "24px", fontWeight: 700, fontFamily: "Roboto, sans-serif", color: "#1C1C1C" }}>Quand souhaitez-vous être rappelé ?</Typography>
                  <Typography sx={{ fontSize: "16px", fontFamily: "Roboto, sans-serif", color: "#000" }}>Choisissez quand vous souhaitez être rappelé.</Typography>
                </Box>
              </Box>
              <Typography sx={{ fontSize: "16px", fontWeight: 500, fontFamily: "Roboto, sans-serif", color: "#000", mb: "8px" }}>Numéro de téléphone</Typography>
              <TextField value={phoneNumber} onChange={(e) => setPhoneNumber(e.target.value)} placeholder="+33 6 76 45 24 76"
                sx={{ mb: "8px", width: isMobile ? "100%" : 356, "& .MuiOutlinedInput-root": { borderRadius: "8px", fontSize: "16px", fontFamily: "Roboto, sans-serif", "& fieldset": { borderColor: "#E1E1E2", borderWidth: "1.5px" }, "&:hover fieldset": { borderColor: "#bbb" } } }}
              />
              <Typography sx={{ fontSize: "16px", color: "#999", fontFamily: "Roboto, sans-serif", mb: "24px" }}>Durée de l'appel : 5 à 10 minutes • Sans engagement</Typography>
              <Box sx={{ display: "flex", gap: "16px", flexDirection: isMobile ? "column" : "row", alignItems: "flex-start" }}>
                <Box sx={{ display: "flex", flexDirection: "column", gap: "8px", flex: 1 }}>
                  {["Maintenant", "Dans 1 heure", "Dans 4 heures", "Dans 24 heures"].map((option) => (
                    <Box key={option} onClick={() => setPhoneTime(option)} sx={{ display: "flex", alignItems: "center", gap: "16px", px: "16px", py: "14px", border: "1.5px solid #E1E1E2", borderRadius: "8px", bgcolor: "#fff", cursor: "pointer", "&:hover": { bgcolor: "#FAFAFA" } }}>
                      <Box sx={{ width: 22, height: 22, borderRadius: "50%", border: phoneTime === option ? "none" : "1.5px solid #E1E1E2", bgcolor: phoneTime === option ? "#3F5EFB" : "transparent", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                        {phoneTime === option && <Box sx={{ width: 10, height: 10, borderRadius: "50%", bgcolor: "#fff" }} />}
                      </Box>
                      <Typography sx={{ fontSize: "16px", fontFamily: "Roboto, sans-serif", color: phoneTime === option ? "#000" : "#999" }}>{option}</Typography>
                    </Box>
                  ))}
                </Box>
                <Box sx={{ flex: 1 }}>
                  <TextField fullWidth multiline rows={5} placeholder="Quelque chose à rajouter ?" value={phoneNote} onChange={(e) => setPhoneNote(e.target.value)}
                    sx={{ mb: "8px", "& .MuiOutlinedInput-root": { borderRadius: "8px", bgcolor: "#fff", fontSize: "16px", fontFamily: "Roboto, sans-serif", fontStyle: "italic", "& fieldset": { borderColor: "#E1E1E2", borderWidth: "1.5px" }, "&:hover fieldset": { borderColor: "#bbb" } } }}
                  />
                  <Typography sx={{ fontSize: "16px", color: "#999", fontFamily: "Roboto, sans-serif", mb: "16px" }}>100 caractères maximum*</Typography>
                  <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                    <Box onClick={handleSendPhone} sx={{ px: "24px", py: "10px", bgcolor: phoneSending || !phoneNumber.trim() ? "#A0AFFD" : "#3F5EFB", borderRadius: "6px", cursor: phoneSending || !phoneNumber.trim() ? "default" : "pointer", fontSize: "16px", fontWeight: 500, fontFamily: "Instrument Sans, sans-serif", color: "#fff", "&:hover": { opacity: phoneSending || !phoneNumber.trim() ? 1 : 0.9 } }}>
                      {phoneSending ? "Envoi…" : "Confirmer le rappel"}
                    </Box>
                  </Box>
                </Box>
              </Box>
            </>
          )}
        </Box>
      </Dialog>

      {/* Main drawer */}
      <Drawer
        anchor="right"
        open={open}
        onClose={onClose}
        slotProps={{
          paper: {
            sx: {
              width: isMobile ? "100%" : 496,
              borderRadius: isMobile ? 0 : "8px 0 0 8px",
              boxShadow: "0px 0px 30px 25px rgba(0,0,0,0.03)",
              p: 0,
              overflow: "hidden",
            },
          },
        }}
      >
        {/* Header */}
        <Box sx={{ pt: "28px", px: "35px", pb: 0 }}>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end", mb: "28px" }}>
            <Box onClick={onClose} sx={{ display: "flex", alignItems: "center", gap: 1, cursor: "pointer", "&:hover": { opacity: 0.7 } }}>
              <Typography sx={{ fontSize: "16px", fontWeight: 500, color: "#999", fontFamily: "Roboto, sans-serif" }}>Fermer</Typography>
              <X size={22} color="#999" />
            </Box>
          </Box>
          <Typography sx={{ fontSize: "24px", fontWeight: 700, fontFamily: "Montserrat, sans-serif", color: "#000", mb: "28px" }}>On vous aide</Typography>
          <Divider />
        </Box>

        {/* Options */}
        <Box sx={{ px: "25px", pt: "24px", display: "flex", flexDirection: "column", gap: "12px" }}>
          {options.map((opt) => (
            <Box
              key={opt.title}
              onClick={() => handleOptionClick(opt.key)}
              sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", p: "20px", borderRadius: "16px", border: "1px solid #F0F0F0", bgcolor: "#fff", cursor: "pointer", textDecoration: "none", "&:hover": { bgcolor: "#FAFAFA" } }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: "19px" }}>
                <Box sx={{ width: 40, height: 40, borderRadius: "20px", bgcolor: "#F0F3FF", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, boxShadow: "0px 0px 24px rgba(0,0,0,0.03)" }}>
                  {opt.icon}
                </Box>
                <Box>
                  <Typography sx={{ fontSize: "16px", fontWeight: 600, color: "#000", fontFamily: "Roboto, sans-serif", letterSpacing: "0.16px" }}>{opt.title}</Typography>
                  <Typography sx={{ fontSize: "16px", fontWeight: 400, color: "#000", fontFamily: "Roboto, sans-serif", letterSpacing: "0.16px" }}>{opt.subtitle}</Typography>
                </Box>
              </Box>
              <CaretCircleRight size={20} color="#000" weight="regular" />
            </Box>
          ))}
        </Box>
      </Drawer>
    </>
  );
}
